import { useLocation } from '@remix-run/react';
import { useEffect, useRef } from 'react';

import { useCustomerSafe } from '~/context/CustomerContext';
import { pushVirtualPageview } from '~/gtm/dataLayer';
import { createUserData } from '~/gtm/events/user';
import { usePageDimensions } from '~/gtm/hooks/usePageDimensions';

/**
 * Component that tracks virtual pageviews and page dimensions
 * Should be placed inside the provider tree but before Outlet
 */
export function GTMPageTracker(): null {
  const location = useLocation();
  const dimensions = usePageDimensions();
  const customerContext = useCustomerSafe();
  const customer = customerContext?.customer ?? null;
  const customerLoading = customerContext?.loading ?? false;
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip if this is the same path (prevents double-firing on hydration)
    const currentPath = location.pathname + location.search;
    if (lastPathRef.current === currentPath) return;

    // Wait for customer context and data to load before firing
    if (customerContext && customerLoading) return;

    lastPathRef.current = currentPath;

    // Build base dimensions
    const pageDimensions: Record<string, unknown> = {
      visitorType: dimensions.visitorType,
      visitorLoginState: dimensions.visitorLoginState,
      visitorState: dimensions.visitorState,
      userID: dimensions.userID,
      clientID: dimensions.clientID,
      pageType: dimensions.pageType,
      language: dimensions.language,
      currency: dimensions.currency,
      breadcrumbsPath: dimensions.breadcrumbsPath,
    };

    // For logged-in users, include user_data in the pageview (per spec 19.7)
    if (customer && dimensions.visitorLoginState === 'user') {
      const userId = customer['@id']?.split('/').pop();
      const userData = createUserData({
        userId,
        email: customer.email || undefined,
        phone: customer.phoneNumber || undefined,
        firstName: customer.firstName || undefined,
        lastName: customer.lastName || undefined,
      });

      // Only include user_data if we have at least email
      if (userData.email_address) {
        pageDimensions.user_data = userData;
      }
    }

    // Push virtual pageview with dimensions (and user_data for logged-in users)
    pushVirtualPageview(location.pathname + location.search, document.title, pageDimensions);
  }, [location.pathname, location.search, dimensions, customer, customerLoading, customerContext]);

  return null;
}
